/**
 * src/app/marketplace/components/Disconnected.js
 * ---------------------------------------------------------------------------
 * Intuit's Disconnect URL — where the user is redirected after they disconnect app from their accounting platform.
 * ---------------------------------------------------------------------------
 */

import React, { useEffect, useRef, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { getProvider } from '../config/providers';
import useMarketplaceSession from '../hooks/useMarketplaceSession';
import { probe, disconnect } from '../hooks/marketplaceSessionStore';
import MarketplaceResolving from './MarketplaceResolving';

const ATTEMPT_KEY_PREFIX = 'mpDisconnectSignIn:';

export default function Disconnected() {
	const { provider: providerKey } = useParams();
	const provider = getProvider(providerKey);
	const session = useMarketplaceSession();

	const selfPath = `/marketplace/${providerKey}/disconnected`;
	const connectPath = `/marketplace/${providerKey}/connect`;
	const attemptKey = `${ATTEMPT_KEY_PREFIX}${providerKey}`;

	const [done, setDone] = useState(false);
	const started = useRef(false);

	useEffect(() => {
		probe(providerKey);
	}, [providerKey]);

	useEffect(() => {
		if (!session.confirmed || started.current) return;

		started.current = true;

		async function revoke() {
			try {
				await disconnect(provider.disconnectPath);
			} catch (err) {
			}

			forgetSignInAttempt(attemptKey);
			setDone(true);
		}

		// Identified — revoke on our side
		if (session.signedIn) {
			revoke();
			return;
		}

		if (hasSignInAttempt(attemptKey)) {
			setDone(true);
			return;
		}

		rememberSignInAttempt(attemptKey);

		window.location.replace(`${connectPath}?ref=quickbooks&next=${encodeURIComponent(selfPath)}`);
	}, [session.confirmed, session.signedIn, provider.disconnectPath, attemptKey, connectPath, selfPath]);

	if (done) return <Redirect to={`${connectPath}?status=disconnected`} />;

	return (
		<MarketplaceResolving provider={provider} message={`Disconnecting ${provider.label}…`} />
	);
}


// ===========================================================================
// Helpers
// ===========================================================================

/**
 * Whether this tab has already bounced through sign-in for this disconnect.
*/
function hasSignInAttempt(key) {
	try {
		return window.sessionStorage.getItem(key) === '1';
	} catch (e) {
		// Storage unavailable
		return false;
	}
}


function rememberSignInAttempt(key) {
	try {
		window.sessionStorage.setItem(key, '1');
	} catch (e) {
		// Storage unavailable
	}
}


function forgetSignInAttempt(key) {
	try {
		window.sessionStorage.removeItem(key);
	} catch (e) {
		// Storage unavailable
	}
}
